import { apiService } from '../../../shared/services/apiServices';
import { AxiosError } from 'axios';
import type { AddClientDetails, MediaResource } from '../types/addClientType';
import { CLIENT_ERROR_MESSAGES } from '../errors/errorMessages';
import clientEndpoints from './clientEndpoints';

export interface AddClientResult {
  success: boolean;
  data?: unknown;
  error?: string;
}

interface CreateClientResponse {
  message: string;
  client: {
    id: string;
  };
  paths: {
    profile?: string;
    docs: string[];
  };
}

const extractErrorMessage = (errorData: unknown, status?: number): string => {
  if (status === 403) {
    return CLIENT_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP;
  }
  if (!errorData) {
    return CLIENT_ERROR_MESSAGES.DEFAULT_ERROR;
  }

  if (typeof errorData === 'string') {
    if (errorData.toLowerCase().includes('email already exists')) {
      return CLIENT_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS;
    }
    return errorData;
  }

  if (typeof errorData === 'object') {
    const apiError = errorData as {
      message?: string;
      error?: string;
    };

    const errorMessage = apiError.message || apiError.error;

    if (errorMessage === 'Internal Server Error') {
      return CLIENT_ERROR_MESSAGES.UNEXPECTED_ERROR;
    }

    if (errorMessage?.toLowerCase().includes('email already exists')) {
      return CLIENT_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS;
    }

    return errorMessage || CLIENT_ERROR_MESSAGES.DEFAULT_ERROR;
  }

  return CLIENT_ERROR_MESSAGES.DEFAULT_ERROR;
};

export const addClient = async (
  clientDetails: AddClientDetails,
  entityId?: string,
  profile?: MediaResource,
  docs?: MediaResource[]
): Promise<AddClientResult> => {
  try {
    const requestBody: {
      id?: string;
      name: string;
      email: string;
      phoneNumber: string;
      countryCode: string;
      nip: string;
      currency: string;
      paymentStatus: string;
      subscriptionPlanId: string;
      addressLine1: string;
      addressLine2?: string;
      city: string;
      state: string;
      zipCode?: string;
      country: string;
      billing_company_name?: string;
      billing_nip?: string;
      billing_regon?: string;
      billing_krs?: string;
      invoice_address_line_1?: string;
      invoice_address_line_2?: string;
      invoice_zip_code?: string;
      invoice_city?: string;
      invoice_province?: string;
      profile?: MediaResource;
      docs: MediaResource[];
    } = {
      name: clientDetails.name.trim(),
      email: clientDetails.email.trim().toLowerCase(),
      phoneNumber: clientDetails.phone.trim(),
      countryCode: clientDetails.countryCode.trim(),
      nip: clientDetails.nip.trim(),
      currency: clientDetails.currency,
      paymentStatus: clientDetails.paymentStatus,
      subscriptionPlanId: clientDetails.subscriptionPlan,
      addressLine1: clientDetails.address1.trim(),
      city: clientDetails.city.trim(),
      state: clientDetails.state.trim(),
      country: clientDetails.country.trim(),
      billing_company_name: clientDetails.billing_company_name?.trim(),
      billing_nip: clientDetails.billing_nip?.trim().replace(/-/g, ''),
      billing_regon: clientDetails.billing_regon?.trim().replace(/\D/g, ''),
      billing_krs: clientDetails.billing_krs?.trim().replace(/\D/g, ''),
      invoice_address_line_1: clientDetails.invoice_address_line_1?.trim(),
      invoice_city: clientDetails.invoice_city?.trim(),
      invoice_province: clientDetails.invoice_province?.trim(),
      docs: docs ?? [],
    };

    if (entityId) {
      requestBody.id = entityId;
    }

    if (clientDetails.address2.trim()) {
      requestBody.addressLine2 = clientDetails.address2.trim();
    }

    if (clientDetails.zip.trim()) {
      requestBody.zipCode = clientDetails.zip.trim();
    }

    if (clientDetails.invoice_address_line_2?.trim()) {
      requestBody.invoice_address_line_2 =
        clientDetails.invoice_address_line_2.trim();
    }

    if (clientDetails.invoice_zip_code?.trim()) {
      requestBody.invoice_zip_code = clientDetails.invoice_zip_code.trim();
    }

    if (profile) {
      requestBody.profile = profile;
    }

    const createResult = await apiService.postAPI(
      clientEndpoints.CLIENTS,
      requestBody,
      { skipGlobalErrorRedirect: true }
    );

    if (createResult.error) {
      const errorMessage = extractErrorMessage(
        createResult.data,
        createResult.status
      );
      return {
        success: false,
        error: createResult.isNetworkError
          ? CLIENT_ERROR_MESSAGES.NETWORK_ERROR
          : errorMessage,
      };
    }

    return {
      success: true,
      data: createResult.data as CreateClientResponse,
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      if (isNetworkError) {
        return { success: false, error: CLIENT_ERROR_MESSAGES.NETWORK_ERROR };
      }
      const errorMessage = extractErrorMessage(error.response?.data);
      return { success: false, error: errorMessage };
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : CLIENT_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
